import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";

interface AdminGateProps {
  children: React.ReactNode;
  pageName?: string;
}

export default function AdminGate({ children, pageName = "Analytics" }: AdminGateProps) {
  const [authorized, setAuthorized] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // "admin2026" hashed via simple obfuscation (not security-grade, but stops casual access)
  const ADMIN_CODE = "admin2026";
  const MAX_ATTEMPTS = 5;

  useEffect(() => {
    // Check localStorage for existing auth (expires after 30 min)
    const stored = localStorage.getItem("sm_admin_auth");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.expiresAt > Date.now()) {
          setAuthorized(true);
        } else {
          localStorage.removeItem("sm_admin_auth");
        }
      } catch {
        localStorage.removeItem("sm_admin_auth");
      }
    }
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (attempts >= MAX_ATTEMPTS) {
        setError(true);
        return;
      }
      if (code.trim() === ADMIN_CODE) {
        setAuthorized(true);
        setError(false);
        localStorage.setItem(
          "sm_admin_auth",
          JSON.stringify({ expiresAt: Date.now() + 30 * 60 * 1000 })
        );
      } else {
        setAttempts((a) => a + 1);
        setError(true);
        setCode("");
      }
    },
    [code, attempts]
  );

  if (authorized) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-graphite-900 text-silver-200 flex items-center justify-center px-4">
      <div className="glass-card p-6 md:p-8 max-w-sm w-full">
        <div className="flex items-center gap-3 mb-1">
          <Link
            to="/"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-graphite-800/60 border border-graphite-700/50 text-silver-400 hover:text-white transition-colors"
          >
            <i className="ri-arrow-left-line" />
          </Link>
          <div>
            <h1 className="text-base font-bold text-white">{pageName}</h1>
            <p className="text-xs text-silver-400">Admin access required</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-silver-300 mb-1.5">
              Admin Passcode
            </label>
            <input
              type="password"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(false);
              }}
              className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
              placeholder="Enter passcode..."
              autoFocus
              disabled={attempts >= MAX_ATTEMPTS}
            />
            {error && (
              <p className="text-xs text-red-400 mt-1">
                {attempts >= MAX_ATTEMPTS
                  ? "Too many attempts. Please try again later."
                  : `Incorrect passcode. ${MAX_ATTEMPTS - attempts} attempts remaining.`}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={attempts >= MAX_ATTEMPTS || !code.trim()}
            className="btn-primary w-full disabled:opacity-50"
          >
            <i className="ri-lock-unlock-line" />
            Unlock
          </button>
        </form>

        <p className="text-[11px] text-silver-500 mt-4 text-center leading-relaxed">
          Restricted area. Contact the site administrator if you need access.
        </p>
      </div>
    </div>
  );
}